import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Smile, Trash2, MapPin, Users, Filter, Image as ImageIcon, CheckCircle, Circle, MessageCircle, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Comment } from 'hooks/useCommentsRealtime';
import { UserPresence } from 'hooks/usePresence';

interface FeedbackPanelProps {
  comments: Comment[];
  activeUsers: UserPresence[];
  currentUserId: string;
  currentUserEmail: string;
  onAddComment: (message: string, pictureId?: string, coordinates?: { x: number; y: number }, parentCommentId?: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onToggleReaction: (commentId: string, emoji: string, userId: string) => Promise<void>;
  onToggleResolve: (commentId: string, userId: string) => Promise<void>;
  currentPictureId?: string;
  isAnnotationMode: boolean;
  pendingAnnotation?: { x: number; y: number } | null;
  onClearAnnotation: () => void;
  onHighlightAnnotation?: (commentId: string | null) => void;
  onClose: () => void;
  replyingTo: string | null;
  setReplyingTo: (commentId: string | null) => void;
  isVideoMode?: boolean; // New prop to indicate video mode
  showAskAI?: boolean;
  onAskAI?: (comment: Comment, replies: Comment[]) => void;
}

const EMOJI_OPTIONS = ['👍', '❤️', '😂', '🎉', '🤔', '👏'];

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
  comments,
  activeUsers,
  currentUserId,
  currentUserEmail: _currentUserEmail,
  onAddComment,
  onDeleteComment,
  onToggleReaction,
  onToggleResolve,
  currentPictureId,
  isAnnotationMode,
  pendingAnnotation,
  onClearAnnotation,
  onHighlightAnnotation,
  onClose,
  replyingTo,
  setReplyingTo,
  isVideoMode = false,
  showAskAI = false,
  onAskAI,
}) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);
  const [filterByCurrentImage, setFilterByCurrentImage] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [clickedHighlight, setClickedHighlight] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Dynamic text based on mode
  const mediaType = isVideoMode ? 'video' : 'image';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const toggleThread = (commentId: string) => {
    const newExpanded = new Set(expandedThreads);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
    }
    setExpandedThreads(newExpanded);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
      await onAddComment(
        message,
        replyingTo ? undefined : currentPictureId,
        replyingTo ? undefined : (pendingAnnotation || undefined),
        replyingTo || undefined
      );
      setMessage('');
      setReplyingTo(null);
      onClearAnnotation();
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
    setClickedHighlight(commentId);
    inputRef.current?.focus();
    
    const newExpanded = new Set(expandedThreads);
    newExpanded.add(commentId);
    setExpandedThreads(newExpanded);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setClickedHighlight(null);
    onHighlightAnnotation?.(null);
    setMessage('');
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getInitials = (email: string) => {
    const name = email.split('@')[0];
    return name.slice(0, 2).toUpperCase();
  };

  // Separate parent comments and replies
  const parentComments = comments.filter(c => !c.parent_comment_id);
  const repliesByParent = comments.reduce((acc, comment) => {
    if (comment.parent_comment_id) {
      if (!acc[comment.parent_comment_id]) {
        acc[comment.parent_comment_id] = [];
      }
      acc[comment.parent_comment_id].push(comment);
    }
    return acc;
  }, {} as Record<string, Comment[]>);

  // Filter comments based on filters
  let displayedComments = parentComments;
  
  if (filterByCurrentImage && currentPictureId) {
    displayedComments = displayedComments.filter(c => c.picture_id === currentPictureId);
  }
  
  if (!showResolved) {
    displayedComments = displayedComments.filter(c => !c.is_resolved);
  }

  const renderComment = (comment: Comment, isReply: boolean = false) => {
    const isOwn = comment.user_id === currentUserId;
    const isOnCurrentImage = comment.picture_id === currentPictureId;
    const replies = repliesByParent[comment.comment_id] || [];
    const isExpanded = expandedThreads.has(comment.comment_id);
    const replyingToThis = replyingTo === comment.comment_id;
    const hasAnnotation = !!comment.coordinates && !isReply;
    const isClickHighlighted = clickedHighlight === comment.comment_id;

    return (
      <div 
        key={comment.comment_id} 
        className={`${isReply ? 'ml-10 mt-2' : ''}`}
        onMouseEnter={() => {
          if (hasAnnotation && !isClickHighlighted) {
            onHighlightAnnotation?.(comment.comment_id);
          }
        }}
        onMouseLeave={() => {
          if (hasAnnotation && !isClickHighlighted) {
            onHighlightAnnotation?.(null);
          }
        }}
      >
        <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar */}
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
              isOwn ? 'bg-blue-600' : 'bg-gray-600'
            }`}
          >
            {getInitials(comment.user_email)}
          </div>

          {/* Message Content */}
          <div className={`flex-1 ${isOwn ? 'text-right' : 'text-left'}`}>
            <div
              className={`inline-block max-w-[85%] ${
                isOwn
                  ? 'bg-blue-600 text-white rounded-l-lg rounded-tr-lg'
                  : comment.is_resolved
                  ? 'bg-green-900/30 text-gray-100 rounded-r-lg rounded-tl-lg border border-green-700/50'
                  : 'bg-gray-700 text-gray-100 rounded-r-lg rounded-tl-lg'
              } px-3 py-2 relative ${hasAnnotation ? 'cursor-pointer hover:ring-2 hover:ring-yellow-400' : ''} ${
                isClickHighlighted ? 'ring-2 ring-yellow-500' : ''
              }`}
              onClick={() => {
                if (hasAnnotation) {
                  if (isClickHighlighted) {
                    setClickedHighlight(null);
                    onHighlightAnnotation?.(null);
                  } else {
                    setClickedHighlight(comment.comment_id);
                    onHighlightAnnotation?.(comment.comment_id);
                  }
                }
              }}
              title={hasAnnotation ? (isClickHighlighted ? 'Click to unhighlight' : 'Click to keep highlighted') : ''}
            >
              {/* Resolved badge */}
              {comment.is_resolved && (
                <div className="absolute -top-2 -right-2 bg-green-600 rounded-full p-1">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
              
              {/* Annotation indicator */}
              {hasAnnotation && (
                <div className="absolute -top-2 -left-2 bg-yellow-500 rounded-full p-1">
                  <MapPin className="w-3 h-3 text-white" />
                </div>
              )}

              {!isOwn && (
                <p className="text-xs font-semibold text-blue-400 mb-1">
                  {comment.user_name || comment.user_email.split('@')[0]}
                </p>
              )}
              <p className="text-sm break-words">{comment.message}</p>
              
              {/* Image context badge */}
              {!isReply && comment.picture_id && !filterByCurrentImage && (
                <div className={`flex items-center gap-1 mt-1 text-xs ${isOnCurrentImage ? 'opacity-75' : 'opacity-50'}`}>
                  <ImageIcon className="w-3 h-3" />
                  <span>
                    {isOnCurrentImage ? `On this ${mediaType}` : `On different ${mediaType}`}
                  </span>
                </div>
              )}
            </div>

            {/* Actions row */}
            <div className="flex items-center gap-2 mt-1 px-1 flex-wrap">
              {/* Reactions */}
              {comment.reactions && Object.keys(comment.reactions).length > 0 && (
                <div className="flex gap-1">
                  {Object.entries(comment.reactions).map(([emoji, userIds]) => (
                    <button
                      key={emoji}
                      onClick={() => onToggleReaction(comment.comment_id, emoji, currentUserId)}
                      className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                        userIds.includes(currentUserId)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {emoji} {userIds.length}
                    </button>
                  ))}
                </div>
              )}

              {/* Reaction Picker */}
              <div className="relative">
                <button
                  onClick={() =>
                    setShowEmojiPicker(
                      showEmojiPicker === comment.comment_id ? null : comment.comment_id
                    )
                  }
                  className="text-gray-400 hover:text-gray-300 transition-colors"
                  title="Add reaction"
                >
                  <Smile className="w-4 h-4" />
                </button>

                {showEmojiPicker === comment.comment_id && (
                  <div className="absolute bottom-6 left-0 bg-gray-700 border border-gray-600 rounded-lg p-2 flex gap-1 shadow-xl z-10">
                    {EMOJI_OPTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => {
                          onToggleReaction(comment.comment_id, emoji, currentUserId);
                          setShowEmojiPicker(null);
                        }}
                        className="text-lg hover:scale-125 transition-transform"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply button */}
              {!isReply && (
                <button
                  onClick={() => handleReply(comment.comment_id)}
                  className="text-gray-400 hover:text-gray-300 transition-colors flex items-center gap-1"
                  title="Reply"
                >
                  <MessageCircle className="w-4 h-4" />
                  {replies.length > 0 && (
                    <span className="text-xs">{replies.length}</span>
                  )}
                </button>
              )}

              {/* Resolve/Unresolve button */}
              {!isReply && (
                <button
                  onClick={() => onToggleResolve(comment.comment_id, currentUserId)}
                  className={`transition-colors flex items-center gap-1 ${
                    comment.is_resolved
                      ? 'text-green-400 hover:text-green-300'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                  title={comment.is_resolved ? 'Unresolve' : 'Resolve'}
                >
                  {comment.is_resolved ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Ask AI button */}
              {!isReply && showAskAI && onAskAI && (
                <button
                  onClick={() => onAskAI(comment, repliesByParent[comment.comment_id] || [])}
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                  title="Ask AI for help"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
              )}

              {/* Delete Button */}
              {isOwn && (
                <button
                  onClick={() => onDeleteComment(comment.comment_id)}
                  className="text-gray-400 hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {/* Timestamp */}
              <span className="text-xs text-gray-500 ml-auto">
                {formatTime(comment.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Thread toggle and replies */}
        {!isReply && replies.length > 0 && (
          <div className="ml-10 mt-2">
            <button
              onClick={() => toggleThread(comment.comment_id)}
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
            </button>

            {isExpanded && (
              <div className="mt-2 space-y-2 border-l-2 border-gray-600 pl-2">
                {replies.map(reply => renderComment(reply, true))}
              </div>
            )}
          </div>
        )}

        {/* Reply indicator */}
        {replyingToThis && (
          <div className="ml-10 mt-2 text-xs text-blue-400 bg-blue-900/20 px-3 py-1 rounded border border-blue-700/30">
            Replying to {comment.user_name || comment.user_email.split('@')[0]}...
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-gray-100">Feedback & Chat</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-700 rounded transition-colors"
          aria-label="Close panel"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Active Users */}
      {activeUsers.length > 0 && (
        <div className="px-4 py-2 bg-gray-700/50 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <Users className="w-4 h-4" />
            <span>Active now:</span>
            <div className="flex -space-x-2">
              {activeUsers.slice(0, 5).map((user) => (
                <div
                  key={user.user_id}
                  className="w-6 h-6 rounded-full bg-blue-600 border-2 border-gray-800 flex items-center justify-center text-xs font-bold text-white"
                  title={user.user_email}
                >
                  {getInitials(user.user_email)}
                </div>
              ))}
              {activeUsers.length > 5 && (
                <div className="w-6 h-6 rounded-full bg-gray-600 border-2 border-gray-800 flex items-center justify-center text-xs font-bold text-white">
                  +{activeUsers.length - 5}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-4 py-2 border-b border-gray-700 space-y-2 flex-shrink-0">
        {currentPictureId && (
          <button
            onClick={() => setFilterByCurrentImage(!filterByCurrentImage)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              filterByCurrentImage
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">
              {filterByCurrentImage ? `This ${mediaType} only` : `All ${mediaType}s`}
            </span>
            <span className="ml-auto text-xs bg-gray-900/50 px-2 py-1 rounded">
              {displayedComments.length}
            </span>
          </button>
        )}

        <button
          onClick={() => setShowResolved(!showResolved)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showResolved
              ? 'bg-green-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">
            {showResolved ? 'Showing resolved' : 'Hiding resolved'}
          </span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {displayedComments.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>
              {filterByCurrentImage
                ? `No comments on this ${mediaType} yet.`
                : !showResolved
                ? 'No unresolved comments.'
                : 'No comments yet. Start the conversation!'}
            </p>
          </div>
        ) : (
          displayedComments.map((comment) => renderComment(comment))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700 flex-shrink-0">
        {!currentPictureId && !replyingTo && (
          <div className="mb-2 text-xs text-yellow-400 flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            <span>Select {isVideoMode ? 'a video' : 'an image'} to leave comments</span>
          </div>
        )}
        {replyingTo && (
          <div className="mb-2 flex items-center justify-between text-xs bg-blue-900/20 px-3 py-2 rounded border border-blue-700/30">
            <span className="text-blue-400">Replying to thread</span>
            <button
              type="button"
              onClick={cancelReply}
              className="text-red-400 hover:text-red-300"
            >
              Cancel
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              !currentPictureId && !replyingTo
                ? `Select ${isVideoMode ? 'a video' : 'an image'} first...`
                : replyingTo
                ? 'Type your reply...'
                : pendingAnnotation
                ? 'Add a comment to this point...'
                : isAnnotationMode
                ? `Click on ${mediaType} to pin comment...`
                : isVideoMode
                ? 'Type your comment (timestamp will be added)...'
                : 'Type your message...'
            }
            disabled={!currentPictureId && !replyingTo}
            className="flex-1 bg-gray-700 border border-gray-600 text-gray-100 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={!message.trim() || (!currentPictureId && !replyingTo)}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </>
  );
};

export default FeedbackPanel;
